import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  getDecisionCounts,
  getTendersWithInteraction,
  upsertInteractionWithDecisionStatus,
} from '../../../shared/api/magellan/interaction';
import type { GetTendersWithInteractionResponseDTO } from '../../../shared/api/magellan/interaction/dto';
import {
  upsertInteractionWithNogo,
  upsertInteractionWithResults,
} from '../../../shared/api/magellan/tender';
import { displayDecisionNotification } from '../../../shared/components/notifications/decisionNotifications';
import { DecisionStatus } from '../../../shared/entities/Interaction';
import { useGlobalSearchFilter } from './useGlobalSearchFilter.hook';
import { usePipelineFilters } from './usePipelineFilters.hook';
import { useTabsStatus } from './useTabs.hook';
import { buildParams } from './useTendersWithInteractionQuery.hook';

export const EXIT_ANIMATION_DURATION = 300;

export function useDecisionStatusMutation() {
  const queryClient = useQueryClient();
  const { activeTabStatus } = useTabsStatus();
  const { filters } = usePipelineFilters();
  const { globalSearchFilter } = useGlobalSearchFilter();

  const params = buildParams({
    decisionStatus: activeTabStatus,
    globalSearchFilter,
    ...filters,
  });
  const tendersWithInteractionQueryKey = [
    getTendersWithInteraction.name,
    params,
  ];

  const mutation = useMutation({
    mutationFn: async ({
      tenderId,
      status,
      reason,
      winningAmount,
    }: {
      tenderId: number;
      status: DecisionStatus;
      reason?: string;
      winningAmount?: number;
    }) => await upsertDecision({ tenderId, status, reason, winningAmount }),
    onSuccess: (_data, { tenderId, status }) => {
      queryClient.invalidateQueries({ queryKey: [getDecisionCounts.name] });
      triggerExitTransition(tenderId, status);
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: tendersWithInteractionQueryKey,
        });
      }, EXIT_ANIMATION_DURATION);
      displayDecisionNotification(status);
    },
  });

  const triggerExitTransition = (tenderId: number, status: DecisionStatus) => {
    const currentData = queryClient.getQueryData<{
      pages: GetTendersWithInteractionResponseDTO[];
    }>(tendersWithInteractionQueryKey);

    if (!currentData) {
      return;
    }

    const tenders = [...currentData.pages.flatMap(page => page.tenders)];
    const index = tenders.findIndex(tender => tender.id === tenderId);

    const rejectionStatuses = [DecisionStatus.NOGO, DecisionStatus.LOSS];
    tenders[index].transform = rejectionStatuses.includes(status)
      ? 'translateX(-100%)'
      : 'translateX(100%)';
  };

  return mutation;
}

function upsertDecision({
  tenderId,
  status,
  reason,
  winningAmount,
}: {
  tenderId: number;
  status: DecisionStatus;
  reason?: string;
  winningAmount?: number;
}) {
  switch (status) {
    case DecisionStatus.NOGO:
      if (!reason) {
        throw new Error('No reason provided for nogo');
      }
      return upsertInteractionWithNogo(tenderId, reason);
    case DecisionStatus.LOSS:
    case DecisionStatus.WIN:
      if (!reason) {
        throw new Error(`No reason provided for a ${status}`);
      }
      return upsertInteractionWithResults(
        tenderId,
        reason,
        status,
        winningAmount,
      );

    default:
      return upsertInteractionWithDecisionStatus(tenderId, status);
  }
}
